import { Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { ScrollDismissBoundary } from "app/pages/sales-intelligence/common-components/scroll-dismiss/ScrollDismissBoundary";
import type { TriggeredRule } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import { LeadPriorityScore } from "app/pages/sales-intelligence/domain/leads/types/lead-priority";
import useLeadScoreTooltipController from "app/pages/sales-intelligence/modules/lead-scoring-agent/hooks/useLeadScoreTooltipController";

import LeadScoreChip from "../../components/LeadScoreChip/LeadScoreChip";
import LeadScoreCountBadge from "../../components/LeadScoreChip/LeadScoreCountBadge";

import LeadScoreTooltipContainer from "./LeadScoreTooltipContainer";
import { StyledLeadPriorityTooltip } from "./styles";

const DEFAULT_TRIGGERED_RULES: readonly TriggeredRule[] = [];

type Props = {
    score?: LeadPriorityScore;
    triggeredRules?: readonly TriggeredRule[];
    domain: string;
    onClick?: (isTooltipOpen: boolean) => void;
};

const LeadScoreAgentContainer = ({
    score = LeadPriorityScore.Unlock,
    triggeredRules = DEFAULT_TRIGGERED_RULES,
    domain,
    onClick,
}: Props) => {
    const rulesCount = triggeredRules.length;

    const { isOpen, currentStep, openedFrom, onChipClick, onBadgeClick, onPrevious, onNext, onClose, onOpenChange } =
        useLeadScoreTooltipController({ total: rulesCount, onClick });

    return (
        <ScrollDismissBoundary
            isOpen={isOpen}
            onClose={onClose}
        >
            <StyledLeadPriorityTooltip
                open={isOpen}
                onOpenChange={onOpenChange}
                primaryContent={
                    <LeadScoreTooltipContainer
                        domain={domain}
                        triggeredRules={triggeredRules}
                        currentStep={currentStep}
                        fallbackScore={score}
                        onPrevious={onPrevious}
                        onNext={onNext}
                        onClose={onClose}
                    />
                }
                primaryPlacement="right"
                secondaryPlacement="top"
                secondaryContent={translate("si.lead_score_agent.chip_tooltip")}
            >
                <Flex
                    inline
                    alignItems="center"
                    gap="4px"
                >
                    <LeadScoreChip
                        score={score}
                        hideLabel
                        isClickable
                        isActive={isOpen ? openedFrom === "chip" : false}
                        onClick={onChipClick}
                    />

                    {rulesCount > 1 ? (
                        <LeadScoreCountBadge
                            count={rulesCount - 1}
                            isActive={isOpen ? openedFrom === "badge" : false}
                            onClick={onBadgeClick}
                        />
                    ) : null}
                </Flex>
            </StyledLeadPriorityTooltip>
        </ScrollDismissBoundary>
    );
};

export default LeadScoreAgentContainer;
